import { useState } from 'react'
import { useAtomValue } from 'jotai'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded'
import { alpha } from '@mui/material/styles'

import YearlyFeeCollectionForm from '../forms/yearly-fee-collection'
import { convertFromCents, printPrice, roundPrice } from '@/helpers'
import YearlyFeeDiscountsOverviewList from './discounts/overview-list'
import YearlyFeePayersList from './fee-payers/list'
import { useYearlyFeeCollections } from '@/hooks/api/use-yearly-fee-collection'
import LoadingBox from '@/components/loading-box'
import ErrorBox from '@/components/error-box'
import { selectedMunicipalityIdAtom } from '@/state'
import DetailRow from '../detail-row'

function YearlyFeeCollectionSingle({
    year,
} : {
    year: number,
}) {

    const municipalityId = useAtomValue(selectedMunicipalityIdAtom)

    const [create, setCreate] = useState<boolean>(false)
    const [edit, setEdit] = useState<boolean>(false)

    const { status, data, error } = useYearlyFeeCollections(municipalityId, year)
    
    if (status === 'pending') {
        return (
            <LoadingBox />
        )
    }

    if (error) {
        return <ErrorBox
            error={error}
            message={`Nepovedlo se načíst detaily výběru poplatku pro rok ${year} pro obec s ID ${municipalityId}`}
        />
    }

    const yearlyFeeCollection = data.find(feeCollection => feeCollection.year === year)

    if (!yearlyFeeCollection) {
        return (
            <Box px={2.5}>
                {create ?
                    <YearlyFeeCollectionForm
                        year={year}
                        closeCallback={() => setCreate(false)}
                    />
                    :
                    <Box>
                        <Typography variant="body1" component="p" mb={2}>
                            Podklady pro vybírání poplatku v roce {year}
                        </Typography>
                        <Typography variant="body1" component="p" mb={2}>
                            Podklady jste zatím nevytvořili. Přejete si je vytvořit?
                        </Typography>
                        <Button
                            variant="contained"
                            color="terciary"
                            startIcon={<AddRoundedIcon />}
                            onClick={() => setCreate(true)}
                        >
                            Vytvořit podklady
                        </Button>
                    </Box>
                }
            </Box>
        )
    }

    return (
        <>
            <Box sx={{
                px: 2.5,
                display: 'flex',
                flexDirection: {
                    xs: 'column',
                    lg: 'row',
                },
                gap: 2.5,
            }}>
                <Box flexGrow={1}>
                    {edit ?
                        <YearlyFeeCollectionForm
                            yearlyFeeCollection={yearlyFeeCollection}
                            year={year}
                            closeCallback={() => setEdit(false)}
                        />
                        :
                        <Box sx={(theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: 'customGrey.boxBackgroundLight',
                            p: 2.5,
                            borderRadius: 2.5,
                            position: 'relative',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            flexGrow: 1,
                            overflow: 'hidden',
                        })}>
                            <Box
                                sx={(theme) => ({
                                position: 'absolute',
                                top: -20,
                                right: -50,
                                color: alpha(theme.palette.text.primary, 0.015),
                                zIndex: 0,
                                pointerEvents: 'none',
                                })}
                            >
                                <AttachMoneyRoundedIcon sx={{ fontSize: 200 }} />
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                flexGrow: 1,
                            }}>
                                <DetailRow label="Rok" wider>
                                    <Typography variant="body2" component="span">
                                        {yearlyFeeCollection.year}
                                    </Typography>
                                </DetailRow>
                                <DetailRow label="Sleva z motivačního m. roku" wider>
                                    <Typography variant="body2" component="span">
                                        {yearlyFeeCollection.discounts_from_year}
                                    </Typography>
                                </DetailRow>
                                <DetailRow label="Poplatek bez slevy" wider>
                                    <Typography variant="body2" component="span">
                                        {printPrice(roundPrice(convertFromCents(yearlyFeeCollection.fee_amount_cents)))}
                                    </Typography>
                                </DetailRow>
                                <DetailRow label="Maximální sleva" wider>
                                    <Typography variant="body2" component="span">
                                        {yearlyFeeCollection.max_discount_cents ? printPrice(roundPrice(convertFromCents(yearlyFeeCollection.max_discount_cents))) : 'Bez omezení'}
                                    </Typography>
                                </DetailRow>
                                <DetailRow label="IBAN" wider>
                                    <Typography variant="body2" component="span" sx={{wordBreak: 'break-word'}}>
                                        {yearlyFeeCollection.iban}
                                    </Typography>
                                </DetailRow>
                            </Box>
                            {/* <Box sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                borderRadius: '1rem',
                            }}>
                                <Tooltip title="Upravit">
                                    <IconButton
                                        id="edit-button"
                                        aria-label="edit"
                                        color="terciary"
                                        onClick={() => setEdit(true)}
                                        sx={{ backgroundColor: 'editableOrange.main' }}
                                    >
                                        <EditRoundedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box> */}
                            <Box sx={{
                                px: 2.5,
                                pt: 2,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}>
                                <Button
                                    variant="outlined"
                                    color="terciary"
                                    startIcon={<EditRoundedIcon />}
                                    onClick={() => setEdit(true)}
                                    sx={{
                                        backgroundColor: 'customGrey.boxBackground',
                                    }}
                                >
                                    Upravit
                                </Button>
                            </Box>
                        </Box>
                    }
                </Box>
                <YearlyFeeDiscountsOverviewList year={year} />
            </Box>
            <Box mt={2}>
                <Box p={2.5}>
                    <Typography variant="h3" component="h2">
                        Seznam poplatníků
                    </Typography>
                </Box>
                <YearlyFeePayersList yearlyFeeCollectionId={yearlyFeeCollection.id} yearlyFeeCents={yearlyFeeCollection.fee_amount_cents} />
            </Box>
        </>
    )
}

export default YearlyFeeCollectionSingle
